import React, { useState } from "react";
import { Avatar } from "../Avatar/Avatar";
import ArrowRightIcon from "../../assets/svg/arrow-right-icon";
import VerbacallIcon from "../../assets/svg/verbacall-icon";
import VerbamailIcon from "../../assets/svg/verbamail-icon";
import VerbamessengerIcon from "../../assets/svg/verbamessenger-icon";
import UserIcon from "../../assets/svg/user-icon";
import FlagIconCDN from "../FlagIconCDN/FlagIconCDN";
import StarIcon from "../../assets/svg/star-icon";
import { Tooltip } from "../Tooltip/Tooltip";
import LeadCardShimmer from "./components/LeadCardShimmer";
import { LeadCardProps } from "./type";
import { format, differenceInDays } from "date-fns";
import { getTimeAgo } from "../../utils/functions/date-time";
import VerbachatIcon from "../../assets/svg/verbachat-icon";
import { extractPlainTextFromHtml } from "../../pages/Communications/utils/functions";
import { formatLargeDisplayNumbers } from "../../utils/functions/formatters";
import { countryOptions } from "../../utils/constants/countryOptions";
import VerbasmsIcon from "../../assets/svg/verbasms-icon";
import FlaggedIcon from "../../assets/svg/flagged-icon";
import FbMessengerIcon from "../../assets/svg/fb-messenger-icon";
import InstagramIcon from "../../assets/svg/instagram-icon";
import CheckboxCheckedIcon from "../../assets/svg/checkbox-checked-icon";
import CheckboxUncheckedIcon from "../../assets/svg/checkbox-unchecked-icon";

const LeadCard: React.FC<LeadCardProps> = ({
  leadName = "Anonymous",
  leadId,
  isOnline = false,
  outboundModalities = {},
  inboundModalities = {},
  isHumanAssisted = false,
  sourceCountry,
  latestMessage,
  isSecondaryLead = false,
  sessionCount,
  messageCount,
  hasFeedback,
  timestamp,
  onClick,
  onCopyLeadId,
  isSelected = false,
  className = "",
  loading = false,
  showAvatarShimmer = true,
  showNameShimmer = true,
  showOutboundModalitiesShimmer = true,
  showHumanAssistedShimmer = true,
  showMessageShimmer = true,
  showDateShimmer = true,
  showLeadIdShimmer = true,
  showTagsShimmer = true,
  showInboundModalitiesShimmer = true,
  flaggedCount,
  ticket_count,
  assignedAgents = [],
  isSelectionMode = false,
  isChecked = false,
  onCheckChange,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [leadIdTooltipOpen, setLeadIdTooltipOpen] = useState(false);
  const [ratingTooltipOpen, setRatingTooltipOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const handleLeadIdClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (leadId && onCopyLeadId) {
      onCopyLeadId(leadId);
      setIsCopied(true);
      setLeadIdTooltipOpen(true);
      setTimeout(() => {
        setIsCopied(false);
        setLeadIdTooltipOpen(false);
      }, 2000);
    }
  };

  const handleLeadIdTooltipOpenChange = (open: boolean) => {
    setLeadIdTooltipOpen(open);
    setIsHovering(open);
    if (open) {
      setIsCopied(false);
    }
  };

  const handleRatingTooltipOpenChange = (open: boolean) => {
    setRatingTooltipOpen(open);
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getCountryName = (countryCode: string) => {
    const country = countryOptions.find(
      (option) => option.flag.toLowerCase() === countryCode.toLowerCase(),
    );
    return country?.name || countryCode;
  };

  const hasOutboundModalities = Object.values(outboundModalities).some(Boolean);
  const hasInboundModalities = Object.values(inboundModalities).some(Boolean);

  if (loading) {
    return (
      <LeadCardShimmer
        className={className}
        showAvatarShimmer={showAvatarShimmer}
        showNameShimmer={showNameShimmer}
        showOutboundModalitiesShimmer={showOutboundModalitiesShimmer}
        showHumanAssistedShimmer={showHumanAssistedShimmer}
        showMessageShimmer={showMessageShimmer}
        showDateShimmer={showDateShimmer}
        showLeadIdShimmer={showLeadIdShimmer}
        showTagsShimmer={showTagsShimmer}
        showInboundModalitiesShimmer={showInboundModalitiesShimmer}
      />
    );
  }

  return (
    <div
      className={`w-full dark:hover:bg-hover-effect-dk cursor-pointer border-b border-border-gray dark:border-border-gray-dk px-[12px] py-[16px] flex flex-col gap-[8px] min-w-0 transition-colors duration-200 ${
        isSelected && !isSelectionMode
          ? "bg-card-highlight lead-card-selected"
          : "bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect"
      } ${className}`}
      onClick={isSelectionMode ? () => onCheckChange?.(!isChecked) : onClick}
    >
      <div className="flex items-center justify-between gap-[8px] min-w-0">
        <div className="flex items-center gap-[5px] min-w-0 flex-1">
          {isSelectionMode && (
            <button
              type="button"
              className="shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onCheckChange?.(!isChecked);
              }}
            >
              {isChecked ? (
                <CheckboxCheckedIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
              ) : (
                <CheckboxUncheckedIcon className="w-[18px] h-[18px] text-border-gray dark:text-border-gray-dk" />
              )}
            </button>
          )}
          <div className="shrink-0 relative">
            <Avatar
              size={24}
              text={getInitials(leadName)}
              className="shrink-0"
            />

            <div className="w-[6px] h-[6px] bg-pure-white dark:bg-pure-white-dk rounded-full absolute bottom-0 right-0 flex flex-col items-center justify-center">
              <div
                className={`w-[4px] h-[4px] ${
                  isOnline
                    ? "bg-verba-green-success dark:bg-verba-green-success-dk"
                    : "bg-offline dark:bg-offline-dk"
                } rounded-full`}
              ></div>
            </div>
          </div>
          <Tooltip content={leadName}>
            <p
              className={`text-base ${
                isSelected
                  ? "text-verba-indigo dark:text-verba-indigo-dk font-semibold"
                  : "text-data dark:text-data-dk font-medium "
              } truncate min-w-0`}
            >
              {leadName}
            </p>
          </Tooltip>
        </div>

        <div className="flex items-center gap-[8px] shrink-0">
          {hasOutboundModalities && (
            <div className="max-w-fit flex items-center gap-[4px] bg-hover-effect dark:bg-hover-effect-dk rounded-[4px] px-[5px] py-[3px] text-sm">
              <ArrowRightIcon className="w-3 h-3 text-data dark:text-data-dk shrink-0" />
              {outboundModalities?.call && (
                <VerbacallIcon className="w-3 h-3 text-verba-lavender dark:text-verba-lavender-dk shrink-0" />
              )}
              {outboundModalities?.whatsapp && (
                <VerbamessengerIcon className="w-3 h-3 text-verba-green-success dark:text-verba-green-success-dk shrink-0" />
              )}
              {outboundModalities?.email && (
                <VerbamailIcon className="w-3 h-3 text-verba-mail dark:text-verba-mail-dk shrink-0" />
              )}
              {outboundModalities?.widget && (
                <VerbachatIcon className="w-3 h-3 text-verba-chat dark:text-verba-chat-dk shrink-0" />
              )}
              {outboundModalities?.sms && (
                <VerbasmsIcon className="w-3 h-3 text-verba-sms dark:text-verba-sms-dk shrink-0" />
              )}
            </div>
          )}

          <div className="flex items-center gap-[4px]">
            {isHumanAssisted && (
              <UserIcon className="w-3 h-3 text-dark-gray dark:text-dark-gray-dk shrink-0" />
            )}
            {flaggedCount !== undefined && flaggedCount > 0 && (
              <Tooltip content={`Flagged Responses`}>
                <div className="flex items-center justify-center border border-border-gray dark:border-border-gray-dk bg-hover-effect dark:bg-hover-effect-dk rounded-sm px-[4px] py-px mx-1">
                  <FlaggedIcon className="w-3 h-3 text-verba-red-alert dark:text-verba-red-alert-dk" />
                  <span className="text-sm text-data dark:text-data-dk font-semibold">
                    {flaggedCount}
                  </span>
                </div>
              </Tooltip>
            )}
            {sourceCountry && (
              <Tooltip content={getCountryName(sourceCountry)} side="top">
                <div className="w-[16px] h-[12px] shrink-0">
                  <FlagIconCDN
                    code={sourceCountry.toLowerCase()}
                    flagIconClassName="w-[16px] h-[12px] shrink-0"
                  />
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-[8px] justify-between">
        <div className="text-md text-data dark:text-data-dk font-normal min-w-0">
          {latestMessage ? (
            <p className="overflow-hidden text-ellipsis whitespace-nowrap">
              {extractPlainTextFromHtml(latestMessage, true)}
            </p>
          ) : (
            <div className="h-4"></div>
          )}
        </div>
        <p className="shrink-0 text-md font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
          {timestamp &&
            (differenceInDays(new Date(), new Date(timestamp)) > 6
              ? format(new Date(timestamp), "dd MMM, yy")
              : getTimeAgo(
                  format(new Date(timestamp), "yyyy-MM-dd'T'HH:mm:ss"),
                ))}
        </p>
      </div>

      <div className="flex items-center justify-between min-w-0">
        <div className="flex items-center gap-[4px] min-w-0 flex-1">
          {leadId && (
            <Tooltip
              content={isCopied && !isHovering ? "Copied!" : "Click to copy"}
              open={leadIdTooltipOpen}
              onOpenChange={handleLeadIdTooltipOpenChange}
            >
              <div
                className="shrink-0 text-dark-gray dark:text-dark-gray-dk text-md font-normal cursor-pointer hover:text-data dark:hover:text-data-dk transition-colors"
                onClick={handleLeadIdClick}
              >
                <p>#{leadId}</p>
              </div>
            </Tooltip>
          )}

          {isSecondaryLead && (
            <div className="text-sm text-data dark:text-data-dk font-medium shrink-0 w-4 h-4 bg-tag-gray dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center">
              <p>S</p>
            </div>
          )}

          {(sessionCount ?? 0) > 0 && (
            <Tooltip content={`Total no. of Sessions`}>
              <div className="text-sm text-analytics-safety dark:text-analytics-safety-dk font-medium shrink-0 min-w-4 h-4 px-1 bg-verba-lavender/20 dark:bg-verba-lavender-dk/20 rounded-full flex flex-col items-center justify-center">
                <p>{formatLargeDisplayNumbers(sessionCount || 0)}</p>
              </div>
            </Tooltip>
          )}

          {(messageCount ?? 0) > 0 && (
            <Tooltip content={`Total no. of Messages`}>
              <div className="text-sm text-verba-blue dark:text-verba-blue-dk font-medium shrink-0 min-w-4 h-4 px-1 bg-verba-blue/5 dark:bg-verba-blue-dk/5 rounded-full flex flex-col items-center justify-center">
                <p>{formatLargeDisplayNumbers(messageCount || 0)}</p>
              </div>
            </Tooltip>
          )}

          {(ticket_count ?? 0) > 0 && (
            <Tooltip content={`Total no. of Tickets`}>
              <div className="text-sm text-verba-red-alert dark:text-verba-red-alert-dk font-medium shrink-0 min-w-4 h-4 px-1 bg-primary-red-crimson-dark/10 dark:bg-primary-red-crimson-dk/5 rounded-full flex flex-col items-center justify-center">
                <p>{formatLargeDisplayNumbers(ticket_count || 0)}</p>
              </div>
            </Tooltip>
          )}

          {hasFeedback !== undefined && hasFeedback > 0 && (
            <Tooltip
              content={`Avg. Rating ${hasFeedback}/5`}
              open={ratingTooltipOpen}
              onOpenChange={handleRatingTooltipOpenChange}
            >
              <div className="shrink-0 w-4 h-4 border border-rating-star-border dark:border-rating-star-border-dk bg-rating-star-border/10 dark:bg-rating-star-border-dk/10 rounded-full flex flex-col items-center justify-center">
                <StarIcon className="w-[9px] h-[9px] text-rating-yellow dark:text-rating-yellow-dk" />
              </div>
            </Tooltip>
          )}

          {assignedAgents.length === 1 && (
            <Tooltip
              content={
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.4px] text-gray-placeholder dark:text-gray-placeholder-dk">
                    Assigned Agent
                  </span>

                  <div className="flex items-center gap-1 py-0.5">
                    <Avatar
                      text={assignedAgents[0]?.name}
                      maxChars={1}
                      size={18}
                    />
                    <span className="text-sm font-medium text-pure-white dark:text-pure-white-dk max-w-[100px] truncate">
                      {assignedAgents[0]?.name}
                    </span>
                  </div>
                </div>
              }
              side="bottom"
            >
              <div className="flex items-center gap-1 shrink-0">
                <Avatar text={assignedAgents[0]?.name} maxChars={1} size={18} />
                <span className="text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk max-w-[50px] truncate leading-normal">
                  {assignedAgents[0]?.name}
                </span>
              </div>
            </Tooltip>
          )}

          {assignedAgents.length > 1 && (
            <Tooltip
              side="bottom"
              content={
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.4px] text-gray-placeholder dark:text-gray-placeholder-dk">
                    Assigned Agents
                  </span>
                  <div className="flex flex-col gap-1">
                    {assignedAgents.map((agent) => (
                      <div
                        key={agent.id}
                        className="flex items-center gap-1 py-0.5"
                      >
                        <Avatar text={agent.name} maxChars={1} size={18} />
                        <span className="text-sm font-medium text-pure-white dark:text-pure-white-dk max-w-[100px] truncate">
                          {agent.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              }
            >
              <div className="flex items-center shrink-0">
                {assignedAgents.map((agent, index) => (
                  <div key={agent.id} className={index > 0 ? "ml-[-4px]" : ""}>
                    <Avatar text={agent.name} maxChars={1} size={18} />
                  </div>
                ))}
              </div>
            </Tooltip>
          )}
        </div>

        {hasInboundModalities && (
          <div className="flex items-center gap-[4px] shrink-0">
            {inboundModalities.call && (
              <VerbacallIcon className="w-3 h-3 text-verba-lavender dark:text-verba-lavender-dk shrink-0" />
            )}
            {inboundModalities.whatsapp && (
              <VerbamessengerIcon className="w-3 h-3 text-verba-green-success dark:text-verba-green-success-dk shrink-0" />
            )}
            {inboundModalities.email && (
              <VerbamailIcon className="w-3 h-3 text-verba-mail dark:text-verba-mail-dk shrink-0" />
            )}
            {inboundModalities.widget && (
              <VerbachatIcon className="w-3 h-3 text-verba-chat dark:text-verba-chat-dk shrink-0" />
            )}
            {inboundModalities.sms && (
              <VerbasmsIcon className="w-3 h-3 text-verba-sms dark:text-verba-sms-dk shrink-0" />
            )}
            {inboundModalities.fb_messenger && (
              <FbMessengerIcon className="w-3 h-3.5 text-dark-gray dark:text-dark-gray-fixed shrink-0" />
            )}
            {inboundModalities.instagram && (
              <InstagramIcon className="w-3 h-3 text-dark-gray dark:text-dark-gray-fixed shrink-0" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadCard;
